/**
 * The workspace store's chokepoint for connection text on tabs (security slice 2026-09-28, `Docs/ConnectionSecrets.md`).
 *
 * Producers name a tab's connection with `safeConnectionName` / `safeConnectionServer`, but a tab's title,
 * `connectionName` and `serverHost` are also restored from session files an older build wrote, and new producers can
 * forget. Everything a tab carries into the tab strip, the status bar, recent tabs, the history dialog and the session
 * file passes through here on its way into the store: a descriptor-shaped name or host is described, any secret masked.
 */
import { redactSecretText, safeServerText } from '../../../../types/safeConnectionDisplay';
import type { TabConnectionInfo } from '../types/workspaceTypes';

/**
 * A tab title with any embedded secret masked ("Tablespaces: <name>" titles carry a connection name). A title that is
 * not text at all (a damaged file: an object, an array) becomes empty — it cannot be checked, so it is not kept (review
 * round 4, Fable N5).
 */
export function safeTabTitle<T extends string | undefined>(title: T): T {
  if (title === undefined) return title;
  return (typeof title === 'string' ? redactSecretText(title) : '') as T;
}

/**
 * A (partial) connection info with its name and host made safe; other fields untouched, absent ones stay absent. A name
 * or host that is not text (a damaged file) is dropped (review round 4, Fable N5).
 */
export function safeTabConnectionInfo<T extends Partial<TabConnectionInfo> | undefined>(info: T): T {
  if (!info) return info;
  // Not an object (a damaged file: a string, an array): it cannot be read, so it is not kept (review round 5, N5).
  if (typeof info !== 'object' || Array.isArray(info)) return undefined as T;
  const next = { ...info } as Partial<TabConnectionInfo>;
  if (typeof next.connectionName === 'string') next.connectionName = safeServerText(next.connectionName) || next.connectionName;
  else if (next.connectionName !== undefined) delete next.connectionName;
  if (typeof next.serverHost === 'string') next.serverHost = safeServerText(next.serverHost);
  else if (next.serverHost !== undefined) delete next.serverHost;
  return next as T;
}

/**
 * A restored tab with a safe title and connection info — the same object when nothing needed masking. Total on any
 * shape an older or damaged file holds (review round 2: a malformed record aborted the sanitization of the rest).
 */
export function safeTab<T extends { title: string; connectionInfo?: Partial<TabConnectionInfo> }>(tab: T): T {
  if (!tab || typeof tab !== 'object') return tab;
  const title = safeTabTitle(tab.title);
  const info = tab.connectionInfo;
  const safeInfo = safeTabConnectionInfo(info);
  const readable = !!info && typeof info === 'object' && !Array.isArray(info);
  const unchanged = title === tab.title && (!info
    || (readable && safeInfo?.connectionName === info.connectionName && safeInfo?.serverHost === info.serverHost));
  if (unchanged) return tab;
  const next = { ...tab, title, ...(info ? { connectionInfo: safeInfo } : {}) };
  if (info && !readable) delete (next as { connectionInfo?: unknown }).connectionInfo;
  return next;
}
